import { NavLink, Outlet } from 'react-router-dom'
import { BrandLockup } from './Brand'
import { OrgSwitcher } from './OrgSwitcher'
import { UserMenu } from './UserMenu'

/**
 * Application shell. Dense, no chrome that does not earn its pixels (§10).
 *
 * The `phase` on each entry gated the navigation while the product was being built: a
 * screen ahead of the current phase rendered greyed out rather than as a link, so the
 * nav showed the shape of the finished thing without pretending a screen existed. Every
 * phase has now landed, so nothing is gated — the mechanism is kept because the next
 * screen to be added will want it.
 *
 * Audit is Admin-only and says so itself rather than disappearing from the nav; a
 * missing item reads as "this product has no audit log".
 */
const screens = [
  { to: '/cost-centers', label: 'Cost Centers', phase: 2 },
  { to: '/accounts', label: 'Chart of Accounts', phase: 2 },
  { to: '/drivers', label: 'Cost Drivers', phase: 3 },
  { to: '/mapping', label: 'Account Mapping', phase: 3 },
  { to: '/qc', label: 'QC Dashboard', phase: 5 },
  { to: '/runs', label: 'Run Console', phase: 4 },
  { to: '/results', label: 'Results Explorer', phase: 4 },
  { to: '/occupancy', label: 'Occupancy', phase: 8 },
  { to: '/audit', label: 'Audit', phase: 8 },
  /*
      Last, and in the navigation rather than tucked into the user menu.

      Configuration used to live wherever it was first needed -- bed types behind a toggle
      on Occupancy, schedules under the Run Console, branding in the user menu -- and the
      result was a bed-type editor that had shipped and was reported as missing. A slot
      here is the cost of there being an answer to "where do I change things?".

      The user menu is the wrong home for it: that menu is about WHO you are, and these
      settings belong to the organization rather than to the person signed in.
  */
  { to: '/settings', label: 'Settings', phase: 8 },
]

const CURRENT_PHASE = 8

export function Shell() {
  return (
    <div className="flex h-full flex-col">
      <header
        className="flex items-center gap-5 border-b px-4"
        style={{ background: 'var(--color-panel)', borderColor: 'var(--color-rule)' }}
      >
        <div className="py-2">
          <BrandLockup size={28} />
        </div>

        <nav className="flex items-stretch gap-0.5">
          {screens.map((screen) =>
            screen.phase <= CURRENT_PHASE ? (
              <NavLink
                key={screen.to}
                to={screen.to}
                className={({ isActive }) =>
                  `border-b-2 px-3 py-2.5 text-[13px] transition-colors ${isActive ? 'font-medium' : ''}`
                }
                style={({ isActive }) => ({
                  // The accent marks the one active thing; everything else stays neutral.
                  borderColor: isActive ? 'var(--color-accent)' : 'transparent',
                  color: isActive ? 'var(--color-accent)' : 'var(--color-muted)',
                })}
              >
                {screen.label}
              </NavLink>
            ) : (
              <span
                key={screen.to}
                className="cursor-not-allowed px-3 py-2.5 text-[13px]"
                style={{ color: 'var(--color-faint)' }}
                title={`Arrives in phase ${screen.phase}`}
              >
                {screen.label}
              </span>
            ),
          )}
        </nav>

        <div className="ml-auto flex items-center gap-3">
          <OrgSwitcher />
          <UserMenu />
        </div>
      </header>

      <main className="min-h-0 flex-1 overflow-hidden">
        <Outlet />
      </main>
    </div>
  )
}
